import React from "react";
import { TextField } from "@mui/material";

/**
 * Strips leading zeros from a whole-number string while preserving:
 *   - decimal values ("0.5" → "0.5")
 *   - empty string / just "-" / "-."
 *   - negative whole numbers ("-007" → "-7")
 */
export const stripLeadingZeros = (raw) => {
  if (raw === null || raw === undefined) return "";
  let value = String(raw);
  if (value === "" || value === "-" || value === "." || value === "-.")
    return value;

  const negative = value.startsWith("-");
  if (negative) value = value.slice(1);

  // Preserve decimals starting with 0 (e.g. "0.5")
  if (value.startsWith("0") && !value.startsWith("0.")) {
    value = value.replace(/^0+/, "");
    if (value === "" || value.startsWith(".")) {
      value = "0" + value; // "" → "0", ".5" → "0.5"
    }
  }
  return negative ? `-${value}` : value;
};

/**
 * A drop-in replacement for <TextField type="number" /> that:
 *   - allows the field to be fully cleared (empty string instead of forcing 0)
 *   - removes leading zeros for whole numbers (so "0" + "5" → "5", not "05")
 *   - still emits a numeric value via onChange (number | "" when empty)
 *
 * Props: same as MUI TextField. `value` can be number or string.
 * `onChange(numericValue, event)` — numericValue is Number or "" when empty.
 */
const NumberField = ({ value, onChange, inputProps, ...rest }) => {
  const displayValue =
    value === null || value === undefined || Number.isNaN(value)
      ? ""
      : String(value);

  const handleChange = (e) => {
    const sanitized = stripLeadingZeros(e.target.value);
    // Mirror the sanitized value back to the input
    e.target.value = sanitized;

    let numeric = "";
    if (sanitized !== "" && sanitized !== "-" && sanitized !== ".") {
      const parsed = Number(sanitized);
      numeric = Number.isNaN(parsed) ? "" : parsed;
    }
    onChange && onChange(numeric, e);
  };

  return (
    <TextField
      {...rest}
      type="number"
      value={displayValue}
      onChange={handleChange}
      inputProps={{ step: "any", ...(inputProps || {}) }}
    />
  );
};

export default NumberField;
